import { FolderPlus, FolderInput, FileText, Trash2, Upload } from "lucide-react";
import { useState } from "react";
import { cn } from "../lib/cn";
import type { Doc, Id } from "../../convex/_generated/dataModel";

type FolderFilter = "all" | "unfiled" | Id<"folders">;

export type SidebarDoc = {
  _id: Id<"documents">;
  title: string;
  folderId?: Id<"folders">;
  lastSentenceIndex: number;
};

export function Sidebar({
  docs,
  folders,
  activeDocId,
  activeFolderFilter,
  onFilterChange,
  onSelectDoc,
  onDeleteDoc,
  onCreateFolder,
  onAssignFolder,
  onNewUpload,
}: {
  docs: SidebarDoc[];
  folders: Doc<"folders">[];
  activeDocId: Id<"documents"> | null;
  activeFolderFilter: FolderFilter;
  onFilterChange: (value: FolderFilter) => void;
  onSelectDoc: (id: Id<"documents">) => void;
  onDeleteDoc: (id: Id<"documents">) => void;
  onCreateFolder: (name: string) => void;
  onAssignFolder: (id: Id<"documents">, folderId: Id<"folders"> | null) => void;
  onNewUpload: () => void;
}) {
  const [folderName, setFolderName] = useState("");
  const visible = docs.filter((doc) => {
    if (activeFolderFilter === "all") return true;
    if (activeFolderFilter === "unfiled") return !doc.folderId;
    return doc.folderId === activeFolderFilter;
  });

  return (
    <aside className="glass-panel flex h-screen w-[280px] shrink-0 flex-col border-r border-glass-border">
      <div className="border-b border-glass-border px-5 py-5">
        <div className="flex items-center gap-2">
          <div className="grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br from-primary to-accent">
            <span className="font-display text-lg leading-none">E</span>
          </div>
          <div>
            <div className="font-display text-lg leading-none">Echo Read</div>
            <div className="text-[11px] text-muted-foreground">listen to anything</div>
          </div>
        </div>
        <button
          type="button"
          onClick={onNewUpload}
          className="mt-4 flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-primary to-accent px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          <Upload className="mr-1 h-4 w-4" /> Upload PDF
        </button>
      </div>

      <div className="px-3 py-3">
        <select
          className="h-9 w-full px-2 text-sm"
          value={activeFolderFilter}
          onChange={(event) => onFilterChange(event.target.value as FolderFilter)}
          aria-label="All documents"
        >
          <option value="all">All documents</option>
          <option value="unfiled">Unfiled</option>
          {folders.map((folder) => (
            <option key={folder._id} value={folder._id}>
              {folder.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex-1 overflow-y-auto px-2">
        {visible.length === 0 ? (
          <p className="px-3 py-6 text-center text-xs text-muted-foreground">
            No documents here yet.
          </p>
        ) : (
          visible.map((doc) => (
            <div
              key={doc._id}
              className={cn(
                "group mb-1 flex items-center gap-2 rounded-lg px-2 py-2 transition-colors",
                activeDocId === doc._id ? "bg-white/10" : "hover:bg-white/5",
              )}
            >
              <button
                type="button"
                onClick={() => onSelectDoc(doc._id)}
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate text-sm">{doc.title}</span>
              </button>
              <select
                className="h-7 w-7 appearance-none border-0 bg-transparent p-0 text-[0] text-muted-foreground opacity-60 group-hover:opacity-100"
                aria-label="Assign folder"
                value={doc.folderId ?? "__none"}
                onChange={(event) =>
                  onAssignFolder(
                    doc._id,
                    event.target.value === "__none"
                      ? null
                      : (event.target.value as Id<"folders">),
                  )
                }
              >
                <option value="__none">Unfiled</option>
                {folders.map((folder) => (
                  <option key={folder._id} value={folder._id}>
                    {folder.name}
                  </option>
                ))}
              </select>
              <FolderInput className="pointer-events-none -ml-7 h-3.5 w-3.5 text-muted-foreground opacity-60 group-hover:opacity-100" />
              <button
                type="button"
                onClick={() => onDeleteDoc(doc._id)}
                className="rounded p-1 text-muted-foreground opacity-60 transition-opacity hover:bg-destructive/20 hover:text-destructive hover:opacity-100"
                aria-label="Delete document"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))
        )}
      </div>

      <div className="border-t border-glass-border p-3">
        <div className="mb-2 flex items-center gap-1 text-xs text-muted-foreground">
          <FolderPlus className="h-3.5 w-3.5" /> New class / folder
        </div>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const name = folderName.trim();
            if (name) {
              onCreateFolder(name);
              setFolderName("");
            }
          }}
          className="flex gap-2"
        >
          <input
            type="text"
            value={folderName}
            onChange={(event) => setFolderName(event.target.value)}
            placeholder="e.g. Counseling 501"
            className="h-9 min-w-0 flex-1 px-2 text-sm"
          />
          <button
            type="submit"
            className="h-9 rounded-lg bg-secondary px-3 text-sm text-secondary-foreground"
          >
            Add
          </button>
        </form>
      </div>
    </aside>
  );
}
